import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Square, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { useConfig, useDeleteSession, useSessions, useStopSession } from '@/api/queries'
import type { SessionListItem, SessionSortBy } from '@/api/types'
import { ConfirmDialog } from '@/components/ui/dialog'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Meter,
  Select,
  Skeleton,
  cn,
  complianceTone,
} from '@/components/ui/primitives'
import { PeriodFilters, type PeriodFilterValue } from '@/features/filters/period-filters'
import { SessionStatusBadge } from '@/features/sessions/status-badge'
import { apiErrorLabel, sourceLabel } from '@/shared/labels'
import { canDeleteSessions } from '@/shared/permissions'
import {
  dayRangeToIso,
  formatDateTime,
  formatDuration,
  formatPercent,
  formatSentiment,
} from '@/shared/format'

const searchSchema = z.object({
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  employee_id: z.string().optional(),
  location_id: z.string().optional(),
  status: z.enum(['active', 'completed', 'failed']).optional(),
  source: z.enum(['tablet_mic', 'bt_lavalier', 'badge']).optional(),
  sort_by: z
    .enum(['started_at', 'duration_sec', 'script_compliance', 'sentiment_score'])
    .default('started_at'),
  sort_dir: z.enum(['asc', 'desc']).default('desc'),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(10).max(200).default(25),
})

export const Route = createFileRoute('/_auth/sessions/')({
  validateSearch: searchSchema,
  component: SessionsPage,
})

const SORTABLE: Array<{ key: SessionSortBy; label: string }> = [
  { key: 'started_at', label: 'Дата' },
  { key: 'duration_sec', label: 'Длительность' },
  { key: 'script_compliance', label: 'Соблюдение скрипта' },
  { key: 'sentiment_score', label: 'Тональность' },
]

function SessionsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { me } = Route.useRouteContext()
  const config = useConfig()

  const [stopTarget, setStopTarget] = useState<SessionListItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<SessionListItem | null>(null)

  const offset = (search.page - 1) * search.limit
  const query = useSessions(
    {
      ...dayRangeToIso(search.date_from, search.date_to),
      employee_id: search.employee_id,
      location_id: search.location_id,
      status: search.status,
      source: search.source,
      sort_by: search.sort_by,
      sort_dir: search.sort_dir,
      limit: search.limit,
      offset,
    },
    // Живой режим: пока в выборке есть идущие разговоры, обновляем список сами.
    { refetchInterval: (query) => (hasActive(query.state.data?.items) ? 10_000 : false) },
  )

  const stopSession = useStopSession()
  const deleteSession = useDeleteSession()

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / search.limit))

  const setFilters = (next: PeriodFilterValue) => {
    void navigate({ search: { ...search, ...next, page: 1 }, replace: true })
  }

  const toggleSort = (key: SessionSortBy) => {
    const sameColumn = search.sort_by === key
    void navigate({
      search: {
        ...search,
        sort_by: key,
        sort_dir: sameColumn && search.sort_dir === 'desc' ? 'asc' : 'desc',
        page: 1,
      },
      replace: true,
    })
  }

  const handleStop = () => {
    if (!stopTarget) return
    stopSession.mutate(stopTarget.id, {
      onSuccess: () => {
        toast.success('Разговор остановлен')
        setStopTarget(null)
      },
      onError: (error) => toast.error(apiErrorLabel((error as { detail?: unknown }).detail)),
    })
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    deleteSession.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Разговор удалён')
        setDeleteTarget(null)
      },
      onError: (error) => toast.error(apiErrorLabel((error as { detail?: unknown }).detail)),
    })
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="font-display text-xl font-bold text-ink">Разговоры</h1>
        <p className="mt-0.5 text-[13px] text-ink-subtle">
          {total > 0 ? `Найдено разговоров: ${total}` : 'Записи разговоров с клиентами'}
        </p>
      </header>

      <PeriodFilters
        user={me}
        value={{
          date_from: search.date_from,
          date_to: search.date_to,
          employee_id: search.employee_id,
          location_id: search.location_id,
        }}
        onChange={setFilters}
      >
        <Select
          aria-label="Статус"
          value={search.status ?? ''}
          onChange={(event) =>
            void navigate({
              search: {
                ...search,
                status: (event.target.value || undefined) as typeof search.status,
                page: 1,
              },
              replace: true,
            })
          }
          className="h-9 w-[160px]"
        >
          <option value="">Любой статус</option>
          <option value="active">Идёт запись</option>
          <option value="completed">Завершён</option>
          <option value="failed">Ошибка</option>
        </Select>

        <Select
          aria-label="Источник"
          value={search.source ?? ''}
          onChange={(event) =>
            void navigate({
              search: {
                ...search,
                source: (event.target.value || undefined) as typeof search.source,
                page: 1,
              },
              replace: true,
            })
          }
          className="h-9 w-[190px]"
        >
          <option value="">Любой источник</option>
          {(config.data?.sources ?? []).map((source) => (
            <option key={source} value={source}>
              {sourceLabel(source)}
            </option>
          ))}
        </Select>
      </PeriodFilters>

      <Card>
        {query.isError ? (
          <ErrorState
            message={apiErrorLabel(
              (query.error as { detail?: unknown }).detail,
              'Не удалось загрузить разговоры',
            )}
            onRetry={() => void query.refetch()}
          />
        ) : query.isLoading && !query.data ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="h-10" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Нет разговоров за выбранный период"
            description="Попробуйте изменить фильтры или расширить период."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-[13px] text-ink-subtle">
                  <SortableHeader
                    label="Дата"
                    columnKey="started_at"
                    current={search.sort_by}
                    dir={search.sort_dir}
                    onSort={toggleSort}
                  />
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Сотрудник
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Статус
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    Источник
                  </th>
                  {SORTABLE.slice(1).map((column) => (
                    <SortableHeader
                      key={column.key}
                      label={column.label}
                      columnKey={column.key}
                      current={search.sort_by}
                      dir={search.sort_dir}
                      onSort={toggleSort}
                      align="right"
                    />
                  ))}
                  <th scope="col" className="px-4 py-2.5 text-right font-medium">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((session) => {
                  const percent =
                    session.script_compliance == null ? null : session.script_compliance * 100
                  return (
                    <tr
                      key={session.id}
                      className={cn(
                        'border-b border-line/60 transition-colors last:border-0 hover:bg-surface-muted/60',
                        session.status === 'active' && 'bg-warning-soft/40',
                      )}
                    >
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <Link
                          to="/sessions/$sessionId"
                          params={{ sessionId: session.id }}
                          className="cursor-pointer font-medium text-brand-700 transition-colors hover:text-brand-500 dark:text-brand-300"
                        >
                          {formatDateTime(session.started_at)}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5">
                        {session.employee_full_name ?? (
                          <span className="text-ink-subtle">Сотрудник не найден</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <SessionStatusBadge status={session.status} />
                      </td>
                      <td className="px-4 py-2.5 text-[13px] text-ink-muted">
                        {sourceLabel(session.source)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-[13px] text-ink-muted whitespace-nowrap">
                        {formatDuration(session.duration_sec)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="font-mono text-[13px] text-ink">
                            {formatPercent(session.script_compliance)}
                          </span>
                          {percent != null && (
                            <Meter
                              value={percent}
                              tone={complianceTone(percent)}
                              className="w-12"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-[13px] text-ink-muted">
                        {formatSentiment(session.sentiment_score)}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {session.status === 'active' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Прервать разговор"
                              onClick={() => setStopTarget(session)}
                            >
                              <Square className="size-3.5" />
                            </Button>
                          )}
                          {canDeleteSessions(me) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Удалить разговор"
                              onClick={() => setDeleteTarget(session)}
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {items.length > 0 && (
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
            <div className="flex items-center gap-2 text-[13px] text-ink-subtle">
              Строк на странице
              <Select
                aria-label="Строк на странице"
                value={String(search.limit)}
                onChange={(event) =>
                  void navigate({
                    search: { ...search, limit: Number(event.target.value), page: 1 },
                    replace: true,
                  })
                }
                className="h-8 w-[72px]"
              >
                {[25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[13px] text-ink-subtle">
                Страница {search.page} из {pageCount}
              </span>
              <Button
                size="sm"
                disabled={search.page <= 1}
                onClick={() =>
                  void navigate({ search: { ...search, page: search.page - 1 }, replace: true })
                }
                title="Предыдущая страница"
              >
                <ChevronLeft className="size-3.5" />
              </Button>
              <Button
                size="sm"
                disabled={search.page >= pageCount}
                onClick={() =>
                  void navigate({ search: { ...search, page: search.page + 1 }, replace: true })
                }
                title="Следующая страница"
              >
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </footer>
        )}
      </Card>

      {hasActive(items) && (
        <p className="text-center text-xs text-ink-subtle">
          <Badge tone="warning">Живой режим</Badge> список обновляется автоматически каждые 10
          секунд
        </p>
      )}

      <ConfirmDialog
        open={stopTarget !== null}
        onOpenChange={(open) => !open && setStopTarget(null)}
        title="Прервать этот разговор?"
        description="Запись остановится, аудио и расшифровка сохранятся, метрики посчитаются как для завершённого разговора."
        confirmLabel="Прервать"
        destructive
        loading={stopSession.isPending}
        onConfirm={handleStop}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Удалить этот разговор?"
        description="Разговор скроется из списков и отчётов. Запись и расшифровка останутся в хранилище."
        confirmLabel="Удалить"
        destructive
        loading={deleteSession.isPending}
        onConfirm={handleDelete}
      />
    </div>
  )
}

function hasActive(items: SessionListItem[] | undefined): boolean {
  return (items ?? []).some((session) => session.status === 'active')
}

function SortableHeader({
  label,
  columnKey,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string
  columnKey: SessionSortBy
  current: SessionSortBy
  dir: 'asc' | 'desc'
  onSort: (key: SessionSortBy) => void
  align?: 'left' | 'right'
}) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-2.5 font-medium', align === 'right' ? 'text-right' : 'text-left')}
    >
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        className="inline-flex cursor-pointer items-center gap-1 transition-colors hover:text-ink"
      >
        {label}
        {current === columnKey &&
          (dir === 'desc' ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />)}
      </button>
    </th>
  )
}
